import { useEffect } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './Auth';
import { Icon, Spinner } from './ui';
import type { IconName } from './ui';
import { SignIn } from '../pages/SignIn';
import { PasskeyStep } from '../pages/PasskeyStep';
import { DevicePage } from '../pages/Device';
import { usePendingStepUps } from '../pages/ApproveSignIns';
import { NowPage } from '../pages/Now';
import { WaitingPage } from '../pages/Waiting';
import { ProjectsPage } from '../pages/Projects';
import { BriefPage } from '../pages/Brief';
import { IssuesPage } from '../pages/Issues';
import { AssistantPage } from '../pages/Assistant';
import { SettingsPage } from '../pages/Settings';
import { BrainPage } from '../pages/Brain';
import { APP_VERSION } from '../lib/config';
import { setLastSection } from '../lib/section';
import { gateView } from '../lib/gate';
import { isAuthed } from '../lib/api';

interface NavItem { to: string; label: string; short: string; icon: IconName; badge?: 'waiting' | 'now' }
const NAV: NavItem[] = [
  { to: '/waiting', label: 'Waiting on you', short: 'Waiting', icon: 'waiting', badge: 'waiting' },
  { to: '/issues', label: 'Issues', short: 'Issues', icon: 'issues' },
  { to: '/now', label: 'Happening now', short: 'Now', icon: 'now', badge: 'now' },
  { to: '/projects', label: 'Projects', short: 'Projects', icon: 'projects' },
  { to: '/brief', label: 'Brief', short: 'Brief', icon: 'brief' },
  { to: '/brain', label: 'Brain', short: 'Brain', icon: 'brain' },
  { to: '/assistant', label: 'Assistant', short: 'Chat', icon: 'assistant' },
  { to: '/settings', label: 'Settings', short: 'Settings', icon: 'settings' },
];

function Shell() {
  const { me, counts, toasts, bootError } = useAuth();
  const loc = useLocation();
  useEffect(() => { setLastSection(loc.pathname.split('/')[1] || 'waiting'); }, [loc.pathname]);
  const badge = (n: NavItem): number => (n.badge ? counts[n.badge] : 0);
  const pending = usePendingStepUps(30000);   // small "Approve sign-ins" link while the glasses wait (contract O1)

  return (
    <div className="shell">
      <aside className="sidebar" aria-label="Sections">
        <div className="brand">Helm</div>
        <nav>
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} className={({ isActive }) => `nav-item${isActive ? ' active' : ''}`}>
              <Icon name={n.icon} /><span>{n.label}</span>
              {badge(n) > 0 ? <span className={`badge${n.badge === 'waiting' ? ' badge-hot' : ''}`}>{badge(n)}</span> : null}
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          {me ? (<><div className="who">{me.user.name ?? me.user.id}</div><div className="muted small">{me.company.name} · {me.role}</div></>) : null}
          {bootError ? <div className="error-note small">{bootError}</div> : null}
          <div className="muted small">Web {APP_VERSION}</div>
        </div>
      </aside>
      <main className="main">
        {pending.items.length > 0
          ? <NavLink to="/approve" className="btn small primary signin-pending">Your glasses ask to sign in — approve<span className="badge badge-hot">{pending.items.length}</span></NavLink>
          : null}
        <Routes>
          <Route path="/" element={<Navigate to="/waiting" replace />} />
          <Route path="/waiting" element={<WaitingPage />} />
          <Route path="/issues" element={<IssuesPage />} />
          <Route path="/now" element={<NowPage />} />
          <Route path="/projects/:slug?" element={<ProjectsPage />} />
          <Route path="/brief" element={<BriefPage />} />
          <Route path="/brain" element={<BrainPage />} />
          <Route path="/assistant" element={<AssistantPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="*" element={<Navigate to="/waiting" replace />} />
        </Routes>
      </main>
      <nav className="tabbar" aria-label="Sections">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => `tab${isActive ? ' active' : ''}`}>
            <span className="tab-icon"><Icon name={n.icon} size={20} />{badge(n) > 0 ? <span className={`badge badge-dot${n.badge === 'waiting' ? ' badge-hot' : ''}`}>{badge(n)}</span> : null}</span>
            <span>{n.short}</span>
          </NavLink>
        ))}
      </nav>
      <div className="toasts" aria-live="polite">{toasts.map((t) => <div key={t.id} className={`toast toast-${t.kind}`}>{t.text}</div>)}</div>
    </div>
  );
}

function Gate() {
  const { status } = useAuth();
  const loc = useLocation();
  switch (gateView(status, loc.pathname, isAuthed())) {
    case 'device': return <DevicePage />;   // O1/O3 approve page: needs only /auth/* (lib/gate.ts, H1)
    case 'spinner': return <div className="center-screen"><Spinner label="Signing in…" /></div>;
    case 'signin': return <SignIn />;   // the URL (e.g. /device?code=…) is untouched, so we land back on it
    case 'passkey': return <PasskeyStep />;
    default: return <Shell />;
  }
}

export function App() {
  return <AuthProvider><Gate /></AuthProvider>;
}
